import React, { useMemo } from "react";
import PDFSingleThumbnail from "./component/PDFSingleThumbnail";
import { ds } from "../../../styles/designSystem";
import ActionButton from "../../../component/common/ActionButton";

export default function ProposalCard({ item, pageNumber, onPreview, onDelete, fill = false }) {

    const thumbnailUrl = useMemo(() => {
        const raw = item?.thumbnailPath || item?.path || "";
        if (!raw) return "";
        if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;

        // 안전하게 BASE + path
        const base = process.env.REACT_APP_API_REAL_URL || "";
        // base 끝/ raw 시작 슬래시 정리
        const b = base.endsWith("/") ? base.slice(0, -1) : base;
        const r = raw.startsWith("/") ? raw.slice(1) : raw;
        return `${b}/${r}`;
    }, [item]);

    const handleClickCard = () => onPreview?.(item, pageNumber);

    const handleClickDelete = (e) => {
        e.stopPropagation();
        onDelete?.(item?.id);
    };

    return (
        <div
            onClick={handleClickCard}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleClickCard();
            }}
            style={{
                borderRadius: 16,
                overflow: "hidden",
                width: fill ? "100%" : undefined,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: ds.colors.surface,
                border: `1px solid ${ds.colors.border}`,
                transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                cursor: "pointer",
                // ✅ hover는 아래 onMouseEnter 없이 CSS로 처리하려면 className이 필요하지만,
                // 지금은 inline만 쓰는 형태라 :hover를 못 쓰니, className 방식 추천.
            }}
            className="proposal-card"
        >
            {/* top */}
            <div style={{ padding: 12 }}>
                <div
                    style={{
                        fontSize: 17,
                        fontWeight: 700,
                        color: ds.colors.textPrimary,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        minWidth: 0,
                    }}
                >
                    <div
                        style={{
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                        }}
                        title={item?.name || ""}
                    >
                        {item?.name || "(제목 없음)"}
                    </div>

                    {pageNumber && (
                        <span style={{ fontSize: 12, color: ds.colors.primary, fontWeight: 800, flexShrink: 0 }}>
              p.{pageNumber}
            </span>
                    )}
                </div>

                {!!item?.tags?.length && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {item.tags.slice(0, 8).map((tg, idx) => (
                            <span
                                key={`${tg}-${idx}`}
                                style={{
                                    fontSize: 11,
                                    padding: "4px 10px",
                                    borderRadius: 999,
                                    background: ds.colors.bg,
                                    color: ds.colors.textSecondary,
                                    fontWeight: 800,
                                    border: `1px solid ${ds.colors.border}`,
                                }}
                            >
                {String(tg).trim()}
              </span>
                        ))}
                        {item.tags.length > 8 && (
                            <span
                                style={{
                                    fontSize: 11,
                                    padding: "4px 10px",
                                    borderRadius: 999,
                                    background: ds.colors.primaryBg,
                                    color: ds.colors.primary,
                                    fontWeight: 900,
                                    border: `1px solid ${ds.colors.primaryBorder}`,
                                }}
                            >
                +{item.tags.length - 8}
              </span>
                        )}
                    </div>
                )}
            </div>

            {/*
              썸네일 — 카드 폭에 맞춰 <b>비율로</b> 높이가 정해진다(16:9 슬라이드 기준).

              [왜 고정 높이가 아닌가] 예전엔 카드 높이가 500px 로 박혀 있고 이미지가 object-fit:cover
              였다. 한 줄 2개로 바꿔 폭이 두 배가 되면, 높이는 그대로인 채 넘치는 위아래가 잘려
              슬라이드 가운데 띠만 남는다 — 크게 보려고 열을 줄였는데 오히려 내용이 사라진다.
              비율 상자로 두면 폭이 커진 만큼 세로도 같이 커져 슬라이드가 통째로 보인다.
            */}
            <div
                style={{
                    aspectRatio: "16 / 9",
                    flex: "none",
                    background: ds.colors.bg,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "stretch",
                    justifyContent: "stretch",
                }}
            >
                {pageNumber ? (
                    <div style={{ width: "100%", height: "100%" }}>
                        {/* contain — 상자가 슬라이드 비율(16:9)이라 16:9 원본은 그대로 꽉 찬다.
                            세로형(A4)이 섞여도 잘라내지 않고 페이지 전체를 보여준다.
                            (className 은 Tailwind 가 없어 효력이 없다 — 크기는 컴포넌트가 인라인으로 준다) */}
                        <PDFSingleThumbnail
                            pdfUrl={item?.path}
                            pageNumber={pageNumber}
                            scale={2}
                            objectFit="contain"
                        />
                    </div>
                ) : thumbnailUrl ? (
                    <img
                        src={thumbnailUrl}
                        alt={item?.name || "thumbnail"}
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                    />
                ) : (
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: ds.colors.textMuted,
                            fontWeight: 800,
                            fontSize: 12,
                        }}
                    >
                        미리보기 없음
                    </div>
                )}
            </div>

            {/* bottom */}
            <div
                style={{
                    padding: 10,
                    display: "flex",
                    justifyContent: "flex-end",
                    background: ds.colors.surface,
                    borderTop: `1px solid ${ds.colors.borderLight}`,
                }}
            >
                <ActionButton
                    variant="delete"
                    onClick={handleClickDelete}
                    sx={{
                        fontSize: 12, fontWeight: 900,
                        py: "6px", px: "10px",
                        transition: "background 120ms ease, border-color 120ms ease",
                    }}
                    label="삭제"
                />
            </div>

            {/* ✅ 카드 hover/focus CSS — inline 으로는 :hover 못 하니까 className 으로 */}
            <style>{`
        .proposal-card:hover{
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.10);
          border-color: ${ds.colors.primaryBorder};
        }
        .proposal-card:focus{
          outline: 3px solid ${ds.colors.primaryLight};
          outline-offset: 2px;
        }
      `}</style>
        </div>
    );
}
